import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Tune as AdjustIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  Add as AddIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Refresh as RecalcIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import inventoryItemService, { extractInventoryError } from '../../../../services/inventoryItemService';
import type { WineListLink } from '../../../../services/inventoryItemService';
import inventoryMovementService from '../../../../services/inventoryMovementService';
import inventoryBalanceService, { type InventoryBalanceAggregate } from '../../../../services/inventoryBalanceService';
import venueLocationService, { pickDefaultLocation } from '../../../../services/venueLocationService';
import wineListService from '../../../../services/wineListService';
import wineListItemService from '../../../../services/wineListItemService';
import supplierService from '../../../../services/supplierService';
import * as supplierItemLinkService from '../../../../services/supplierItemLinkService';
import type { SupplierItemLink } from '../../../../services/supplierItemLinkService';
import InventoryQuantityBadge from '../../../../components/venue/InventoryQuantityBadge';
import MovementTypeBadge from '../../../../components/venue/MovementTypeBadge';
import type { InventoryItem, InventoryMovement, Supplier, VenueLocation, WineList } from '../../../../types/venue';

const InventoryItemDetail: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { venueId, defaultCurrency } = useVenue();
  const { itemId } = useParams<{ itemId: string }>();

  const backPath = `/admin/venues/${venueId}/inventory`;

  // ── Item ──────────────────────────────────────────────────────────────────────
  const [item, setItem] = useState<InventoryItem | null>(null);
  const [itemLoading, setItemLoading] = useState(true);
  const [itemError, setItemError] = useState<string | null>(null);

  // ── Balance aggregate ─────────────────────────────────────────────────────────
  const [balanceAggregate, setBalanceAggregate] = useState<InventoryBalanceAggregate | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(true);

  // ── Locations (loaded once on mount for location breakdown + recompute dialog) ─
  const [locations, setLocations] = useState<VenueLocation[]>([]);

  useEffect(() => {
    if (!venueId) return;
    venueLocationService.getLocations(venueId)
      .then(locs => setLocations(locs.filter(l => l.active)))
      .catch(() => setLocations([]));
  }, [venueId]);

  // ── Delete ────────────────────────────────────────────────────────────────────
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // ── Recompute balance ─────────────────────────────────────────────────────────
  const [recomputeOpen, setRecomputeOpen] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [recomputeResult, setRecomputeResult] = useState<{ before: number; after: number; difference: number } | null>(null);
  const [recomputeError, setRecomputeError] = useState<string | null>(null);
  const [recomputeLocationId, setRecomputeLocationId] = useState('');

  useEffect(() => {
    if (!recomputeOpen) return;
    const def = pickDefaultLocation(locations);
    setRecomputeLocationId(def?._id ?? '');
    setRecomputeResult(null);
    setRecomputeError(null);
  }, [recomputeOpen, locations]);

  const handleRecompute = async () => {
    if (!venueId || !itemId || !recomputeLocationId) return;
    setRecomputing(true);
    setRecomputeError(null);
    try {
      const result = await inventoryBalanceService.recomputeBalance(venueId, itemId, recomputeLocationId);
      setRecomputeResult(result);
      inventoryBalanceService.getItemBalances(venueId, itemId)
        .then(setBalanceAggregate)
        .catch(() => { /* non-blocking */ });
    } catch (err: any) {
      setRecomputeError(err?.response?.data?.error?.message ?? 'Failed to recompute balance.');
    } finally {
      setRecomputing(false);
    }
  };

  const handleDelete = async () => {
    if (!venueId || !itemId) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await inventoryItemService.deleteItem(venueId, itemId);
      navigate(backPath);
    } catch (err) {
      setDeleteError(extractInventoryError(err));
      setDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  // ── Wine list links ───────────────────────────────────────────────────────────
  const [wineListLinks, setWineListLinks] = useState<WineListLink[]>([]);
  const [linksLoading, setLinksLoading] = useState(false);

  const loadWineListLinks = useCallback(async () => {
    if (!venueId || !itemId) return;
    setLinksLoading(true);
    try {
      const links = await inventoryItemService.getWineListLinks(venueId, itemId);
      setWineListLinks(links);
    } catch { /* non-blocking */ }
    finally { setLinksLoading(false); }
  }, [venueId, itemId]);

  useEffect(() => { loadWineListLinks(); }, [loadWineListLinks]);

  // Add to Wine List dialog
  const [addToListOpen, setAddToListOpen] = useState(false);
  const [availableLists, setAvailableLists] = useState<WineList[]>([]);
  const [listsLoading, setListsLoading] = useState(false);
  const [selectedListId, setSelectedListId] = useState('');
  const [addVisibility, setAddVisibility] = useState<'HIDDEN' | 'LIVE'>('HIDDEN');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');

  const openAddDialog = async () => {
    setSelectedListId('');
    setAddVisibility('HIDDEN');
    setAddError('');
    setAddToListOpen(true);
    setListsLoading(true);
    try {
      const result = await wineListService.getWineLists(venueId, 1, 100);
      setAvailableLists(result.data.filter(l => l.status !== 'ARCHIVED'));
    } catch { setAvailableLists([]); }
    finally { setListsLoading(false); }
  };

  const handleAddToList = async () => {
    if (!item || !selectedListId) return;
    setAdding(true);
    setAddError('');
    try {
      await wineListItemService.createItem(venueId, selectedListId, {
        wineId: item.wineId,
        inventoryItemId: item._id,
        visibility: addVisibility,
        source: 'MANUAL',
        active: true,
        ...(item.sizeValue != null && { bottleSize: item.sizeValue }),
      } as any);
      setAddToListOpen(false);
      loadWineListLinks();
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? '';
      setAddError(
        err?.response?.status === 409 || msg.toLowerCase().includes('already')
          ? 'This item is already on the selected wine list.'
          : 'Failed to add to wine list. Please try again.'
      );
    } finally { setAdding(false); }
  };

  const handleRemoveFromList = async (link: WineListLink) => {
    if (!window.confirm(`Remove from "${link.wineListName}"?`)) return;
    try {
      await wineListItemService.deleteItem(venueId, link.wineListId, link._id);
      loadWineListLinks();
    } catch { /* TODO: surface error if needed */ }
  };

  // ── Supplier links ────────────────────────────────────────────────────────────
  const [supplierLinks, setSupplierLinks] = useState<SupplierItemLink[]>([]);
  const [supplierLinksLoading, setSupplierLinksLoading] = useState(false);
  const [supplierLinksError, setSupplierLinksError] = useState<string | null>(null);

  // Add supplier dialog
  const [addSupplierOpen, setAddSupplierOpen] = useState(false);
  const [availableSuppliers, setAvailableSuppliers] = useState<Supplier[]>([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [newSupplierId, setNewSupplierId] = useState('');
  const [newSupplierRef, setNewSupplierRef] = useState('');
  const [addSupplierSaving, setAddSupplierSaving] = useState(false);
  const [addSupplierError, setAddSupplierError] = useState('');

  // Edit reference dialog
  const [editRefOpen, setEditRefOpen] = useState(false);
  const [editRefLink, setEditRefLink] = useState<SupplierItemLink | null>(null);
  const [editRefValue, setEditRefValue] = useState('');
  const [editRefSaving, setEditRefSaving] = useState(false);
  const [editRefError, setEditRefError] = useState('');

  const loadSupplierLinks = useCallback(async () => {
    if (!venueId || !itemId) return;
    setSupplierLinksLoading(true);
    setSupplierLinksError(null);
    try {
      const links = await supplierItemLinkService.getLinks(venueId, itemId);
      setSupplierLinks(links);
    } catch {
      setSupplierLinksError('Failed to load supplier links.');
    } finally {
      setSupplierLinksLoading(false);
    }
  }, [venueId, itemId]);

  useEffect(() => { loadSupplierLinks(); }, [loadSupplierLinks]);

  const openAddSupplierDialog = async () => {
    setNewSupplierId('');
    setNewSupplierRef('');
    setAddSupplierError('');
    setAddSupplierOpen(true);
    setSuppliersLoading(true);
    try {
      const result = await supplierService.getAllSuppliers(1, 200, 'name', 'asc');
      setAvailableSuppliers(result.data);
    } catch {
      setAvailableSuppliers([]);
    } finally {
      setSuppliersLoading(false);
    }
  };

  const handleAddSupplier = async () => {
    if (!venueId || !itemId || !newSupplierId) return;
    setAddSupplierSaving(true);
    setAddSupplierError('');
    try {
      await supplierItemLinkService.createLink(venueId, itemId, {
        supplierId: newSupplierId,
        supplierReference: newSupplierRef.trim() || undefined,
      });
      setAddSupplierOpen(false);
      loadSupplierLinks();
    } catch (err: any) {
      const code = err?.response?.data?.error?.code;
      setAddSupplierError(
        code === 'SUPPLIER_ITEM_LINK_DUPLICATE'
          ? 'This supplier is already linked to this item.'
          : 'Failed to add supplier. Please try again.',
      );
    } finally {
      setAddSupplierSaving(false);
    }
  };

  const openEditRef = (link: SupplierItemLink) => {
    setEditRefLink(link);
    setEditRefValue(link.supplierReference ?? '');
    setEditRefError('');
    setEditRefOpen(true);
  };

  const handleEditRef = async () => {
    if (!venueId || !itemId || !editRefLink) return;
    setEditRefSaving(true);
    setEditRefError('');
    try {
      await supplierItemLinkService.updateLink(venueId, itemId, editRefLink._id, {
        supplierReference: editRefValue.trim() || undefined,
      });
      setEditRefOpen(false);
      loadSupplierLinks();
    } catch {
      setEditRefError('Failed to update reference. Please try again.');
    } finally {
      setEditRefSaving(false);
    }
  };

  const handleTogglePreferred = async (link: SupplierItemLink) => {
    if (!venueId || !itemId) return;
    try {
      if (link.isPreferred) {
        await supplierItemLinkService.clearPreferred(venueId, itemId, link._id);
      } else {
        await supplierItemLinkService.setPreferred(venueId, itemId, link._id);
      }
      loadSupplierLinks();
    } catch {
      setSupplierLinksError('Failed to update preferred supplier.');
    }
  };

  const handleToggleActive = async (link: SupplierItemLink, active: boolean) => {
    if (!venueId || !itemId) return;
    try {
      await supplierItemLinkService.updateLink(venueId, itemId, link._id, { active });
      loadSupplierLinks();
    } catch {
      setSupplierLinksError('Failed to update supplier link.');
    }
  };

  // ── Movements ─────────────────────────────────────────────────────────────────
  const [movements, setMovements] = useState<InventoryMovement[]>([]);
  const [movTotal, setMovTotal] = useState(0);
  const [movPage, setMovPage] = useState(0); // 0-based for MUI
  const [movPageSize, setMovPageSize] = useState(25);
  const [movLoading, setMovLoading] = useState(false);
  const [movError, setMovError] = useState<string | null>(null);

  // ── Load item + balance ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!venueId || !itemId) return;
    setItemLoading(true);
    setItemError(null);

    inventoryItemService.getItem(venueId, itemId)
      .then(data => {
        setItem(data);
        setBalanceLoading(true);
        inventoryBalanceService.getItemBalances(venueId, itemId)
          .then(setBalanceAggregate)
          .catch(() => { /* non-blocking — balanceLoading=false triggers empty state */ })
          .finally(() => setBalanceLoading(false));
      })
      .catch(err => setItemError(extractInventoryError(err)))
      .finally(() => setItemLoading(false));
  }, [venueId, itemId]);

  // ── Load movements ────────────────────────────────────────────────────────────
  const loadMovements = useCallback(async () => {
    if (!venueId || !itemId) return;
    setMovLoading(true);
    setMovError(null);
    try {
      const result = await inventoryMovementService.getMovements(
        venueId,
        movPage + 1,
        movPageSize,
        itemId,
      );
      setMovements(result.data ?? []);
      setMovTotal(result.pagination?.totalItems ?? 0);
    } catch (err) {
      setMovError(extractInventoryError(err));
    } finally {
      setMovLoading(false);
    }
  }, [venueId, itemId, movPage, movPageSize]);

  useEffect(() => { loadMovements(); }, [loadMovements]);

  // ── Helpers ───────────────────────────────────────────────────────────────────
  const fmt = (n?: number | null) =>
    n != null
      ? `${defaultCurrency ? `${defaultCurrency} ` : ''}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : '—';

  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });

  const qtyColor = (qty: number) => (qty > 0 ? '#1a7f37' : qty < 0 ? '#c0392b' : 'text.secondary');

  const resolveLocationName = (locationId: string) =>
    locations.find(l => l._id === locationId)?.name ?? locationId;

  // ── Loading state ─────────────────────────────────────────────────────────────
  if (itemLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (itemError || !item) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', alignSelf: 'flex-start', color: theme.palette.text.secondary }}>
          Inventory
        </Button>
        <Alert severity="error">{itemError ?? 'Item not found.'}</Alert>
      </Box>
    );
  }

  const adjustPath = `/admin/venues/${venueId}/inventory/adjust?itemId=${itemId}`;

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Back + actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(backPath)}
          sx={{ textTransform: 'none', color: theme.palette.text.secondary }}
        >
          Inventory
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Post manual adjustment">
            <Button
              variant="contained"
              startIcon={<AdjustIcon />}
              onClick={() => navigate(adjustPath)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Adjust
            </Button>
          </Tooltip>
          <Tooltip title="Edit configuration">
            <IconButton onClick={() => navigate(`/admin/venues/${venueId}/inventory/${itemId}/edit`)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Recompute stock balance">
            <IconButton onClick={() => setRecomputeOpen(true)}>
              <RecalcIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete item">
            <IconButton
              onClick={() => setDeleteConfirm(true)}
              sx={{ color: theme.palette.error.main }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Header card */}
      <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Typography variant="h6" fontWeight={600}>
          {item.displayName}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, mb: 2 }}>
          <Chip
            label={item.type === 'WINE' ? 'Wine item' : 'Generic item'}
            size="small"
            sx={{
              fontSize: '0.68rem',
              height: 20,
              fontWeight: 600,
              color: item.type === 'WINE' ? '#6941c6' : 'text.secondary',
              backgroundColor: item.type === 'WINE' ? 'rgba(105,65,198,0.08)' : 'rgba(0,0,0,0.06)',
              border: 'none',
            }}
          />
          {item.type === 'WINE' && (
            <Typography variant="caption" color="text.secondary">
              {item.wineId ? 'Linked to wine catalog' : 'Wine type · no catalog link'}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {/* Quantity */}
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Total Stock
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              {balanceLoading ? (
                <CircularProgress size={18} />
              ) : (
                <InventoryQuantityBadge
                  quantity={balanceAggregate?.totalQuantity ?? 0}
                  parLevel={item.parLevel}
                  reorderPoint={item.reorderPoint}
                  size="medium"
                />
              )}
            </Box>
          </Box>

          {/* Last cost — taken from most recently updated balance location that has a cost */}
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Last Cost
            </Typography>
            <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
              {fmt(
                balanceAggregate?.locationBreakdown
                  .filter(e => e.lastCost != null)
                  .sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime())[0]
                  ?.lastCost
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary">Most recent receipt cost</Typography>
          </Box>

          {/* Par level */}
          {item.parLevel != null && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Par Level
              </Typography>
              <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>{item.parLevel}</Typography>
            </Box>
          )}

          {/* Reorder point */}
          {item.reorderPoint != null && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Reorder At
              </Typography>
              <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>{item.reorderPoint}</Typography>
            </Box>
          )}

          {/* Size */}
          {item.sizeValue != null && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Size
              </Typography>
              <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                {item.sizeValue} {item.sizeUnit ?? 'ml'}
              </Typography>
            </Box>
          )}

          {/* Source */}
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Source
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip label={item.source} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Stock by Location */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Typography variant="subtitle1" fontWeight={600}>Stock by Location</Typography>
          <Typography variant="caption" color="text.secondary">Stock per location · Total Stock above is the sum of all locations</Typography>
        </Box>
        {balanceLoading ? (
          <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : !balanceAggregate || balanceAggregate.locationBreakdown.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center', color: theme.palette.text.secondary }}>
            <Typography variant="body2" fontWeight={500}>No stock yet</Typography>
            <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
              Receive stock or post a manual adjustment to set the opening balance.
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Location', 'Quantity', 'Last Movement', 'Last Updated'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {balanceAggregate.locationBreakdown.map(entry => (
                <TableRow key={entry.locationId ?? 'unknown'} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {entry.locationId ? resolveLocationName(entry.locationId) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700} sx={{ color: qtyColor(entry.quantity) }}>
                      {entry.quantity}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {entry.lastMovementType
                      ? <MovementTypeBadge type={entry.lastMovementType as any} />
                      : <Typography variant="body2" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {entry.lastUpdated ? fmtDate(entry.lastUpdated) : '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Wine Lists — only for WINE type items */}
      {item.type === 'WINE' && (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <Box
            sx={{
              px: 3,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>Wine Lists</Typography>
              <Typography variant="caption" color="text.secondary">Wine lists this item appears on</Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={openAddDialog}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Add to Wine List
            </Button>
          </Box>

          {linksLoading ? (
            <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : wineListLinks.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center', color: theme.palette.text.secondary }}>
              <Typography variant="body2">Not linked to any wine list yet.</Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Wine List', 'Status', 'Visibility', 'Price', ''].map(h => (
                    <TableCell
                      key={h}
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {wineListLinks.map(link => (
                  <TableRow key={link._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{link.wineListName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={link.wineListStatus}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.68rem', height: 20, textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={link.visibility}
                        size="small"
                        color={link.visibility === 'LIVE' ? 'success' : 'default'}
                        variant={link.visibility === 'LIVE' ? 'filled' : 'outlined'}
                        sx={{ fontSize: '0.68rem', height: 20 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {link.listPrice != null ? `${link.currency ?? '€'}${link.listPrice.toFixed(2)}` : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove from this wine list">
                        <IconButton size="small" onClick={() => handleRemoveFromList(link)} sx={{ color: theme.palette.error.main }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      {/* Suppliers */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Suppliers</Typography>
            <Typography variant="caption" color="text.secondary">Supplier links and receiving memory</Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={openAddSupplierDialog}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Add Supplier
          </Button>
        </Box>

        {supplierLinksError && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="warning" onClose={() => setSupplierLinksError(null)}>{supplierLinksError}</Alert>
          </Box>
        )}

        {supplierLinksLoading ? (
          <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : supplierLinks.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center', color: theme.palette.text.secondary }}>
            <Typography variant="body2">No supplier links yet. Links are created automatically on receiving.</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Supplier', 'Reference', 'Last Cost', 'Last Received', 'Preferred', 'Active', 'Actions'].map(h => (
                  <TableCell
                    key={h}
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {supplierLinks.map(link => (
                <TableRow key={link._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{link.supplierName ?? '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={link.supplierReference ? 'text.primary' : 'text.disabled'}>
                      {link.supplierReference ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {link.lastReceivedCost != null ? fmt(link.lastReceivedCost) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {link.lastReceivedDate
                        ? new Date(link.lastReceivedDate).toLocaleDateString()
                        : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={link.isPreferred ? 'Clear preferred' : 'Set as preferred'}>
                      <IconButton
                        size="small"
                        onClick={() => handleTogglePreferred(link)}
                        sx={{ color: link.isPreferred ? '#f59e0b' : theme.palette.action.disabled }}
                      >
                        {link.isPreferred ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Switch
                      size="small"
                      checked={link.active}
                      onChange={(_, checked) => handleToggleActive(link, checked)}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit reference">
                      <IconButton size="small" onClick={() => openEditRef(link)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Movement History */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Movement History</Typography>
            <Typography variant="caption" color="text.secondary">Append-only — corrections require a reverse adjustment · Transfers appear here as stock-out / stock-in entries</Typography>
          </Box>
          {movTotal > 0 && (
            <Typography variant="caption" color="text.secondary">{movTotal} movements</Typography>
          )}
        </Box>

        {movError && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="warning">{movError}</Alert>
          </Box>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Date', 'Type', 'Qty', 'Unit Cost', 'Total Cost', 'Reference', 'Posted By', 'Notes'].map(h => (
                  <TableCell
                    key={h}
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {movLoading ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={24} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : movements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box sx={{ py: 4, textAlign: 'center', color: theme.palette.text.secondary }}>
                      <Typography variant="body2">
                        No movements yet. Post a manual adjustment to set opening stock.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                movements.map(mov => (
                  <TableRow key={mov._id} hover>
                    <TableCell>
                      <Typography variant="caption" whiteSpace="nowrap">{fmtDate(mov.postedAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      <MovementTypeBadge type={mov.type} />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ color: qtyColor(mov.quantity) }}
                      >
                        {mov.quantity > 0 ? `+${mov.quantity}` : mov.quantity}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{fmt(mov.unitCost)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {mov.unitCost != null ? fmt(mov.quantity * mov.unitCost) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {mov.sourceDocument
                          ? `${mov.sourceDocument.type.replace(/_/g, ' ')}: ${mov.sourceDocument.id.slice(-6)}`
                          : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{mov.postedBy}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{mov.notes ?? '—'}</Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {movTotal > movPageSize && (
          <TablePagination
            component="div"
            count={movTotal}
            page={movPage}
            rowsPerPage={movPageSize}
            rowsPerPageOptions={[25, 50]}
            onPageChange={(_, p) => setMovPage(p)}
            onRowsPerPageChange={e => { setMovPageSize(Number(e.target.value)); setMovPage(0); }}
          />
        )}
      </Paper>

      {deleteError && <Alert severity="error" onClose={() => setDeleteError(null)}>{deleteError}</Alert>}

      {/* Add to Wine List dialog */}
      <Dialog open={addToListOpen} onClose={() => setAddToListOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add to Wine List</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
          {addError && <Alert severity="error">{addError}</Alert>}
          {listsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <FormControl fullWidth size="small">
                <InputLabel>Wine list</InputLabel>
                <Select
                  value={selectedListId}
                  label="Wine list"
                  onChange={e => setSelectedListId(e.target.value)}
                >
                  {availableLists.map(l => (
                    <MenuItem key={l._id} value={l._id}>{l.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Visibility</InputLabel>
                <Select
                  value={addVisibility}
                  label="Visibility"
                  onChange={e => setAddVisibility(e.target.value as 'HIDDEN' | 'LIVE')}
                >
                  <MenuItem value="HIDDEN">Hidden</MenuItem>
                  <MenuItem value="LIVE">Live</MenuItem>
                </Select>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddToListOpen(false)} sx={{ textTransform: 'none' }} disabled={adding}>
            Cancel
          </Button>
          <Button
            onClick={handleAddToList}
            variant="contained"
            disabled={adding || !selectedListId}
            sx={{ textTransform: 'none', fontWeight: 600 }}
            startIcon={adding ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {adding ? 'Adding…' : 'Add to Wine List'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Supplier dialog */}
      <Dialog open={addSupplierOpen} onClose={() => setAddSupplierOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Supplier</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
          {addSupplierError && <Alert severity="error">{addSupplierError}</Alert>}
          {suppliersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <FormControl fullWidth size="small" required>
                <InputLabel>Supplier</InputLabel>
                <Select
                  value={newSupplierId}
                  label="Supplier"
                  onChange={e => setNewSupplierId(e.target.value)}
                >
                  {availableSuppliers.map(s => (
                    <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Supplier Reference"
                size="small"
                fullWidth
                value={newSupplierRef}
                onChange={e => setNewSupplierRef(e.target.value)}
                placeholder="e.g. SKU or account ref (optional)"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddSupplierOpen(false)} sx={{ textTransform: 'none' }} disabled={addSupplierSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleAddSupplier}
            variant="contained"
            disabled={addSupplierSaving || !newSupplierId}
            sx={{ textTransform: 'none', fontWeight: 600 }}
            startIcon={addSupplierSaving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {addSupplierSaving ? 'Adding…' : 'Add Supplier'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Reference dialog */}
      <Dialog open={editRefOpen} onClose={() => setEditRefOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Supplier Reference</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
          {editRefError && <Alert severity="error">{editRefError}</Alert>}
          <TextField
            label="Supplier Reference"
            size="small"
            fullWidth
            value={editRefValue}
            onChange={e => setEditRefValue(e.target.value)}
            placeholder="e.g. SKU or account ref"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRefOpen(false)} sx={{ textTransform: 'none' }} disabled={editRefSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleEditRef}
            variant="contained"
            disabled={editRefSaving}
            sx={{ textTransform: 'none', fontWeight: 600 }}
            startIcon={editRefSaving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {editRefSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recompute balance dialog */}
      <Dialog open={recomputeOpen} onClose={() => setRecomputeOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Recompute Stock Balance</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          {!recomputeResult ? (
            <>
              <FormControl fullWidth size="small" required>
                <InputLabel>Location</InputLabel>
                <Select
                  value={recomputeLocationId}
                  label="Location"
                  onChange={e => setRecomputeLocationId(e.target.value)}
                >
                  {locations.map(l => (
                    <MenuItem key={l._id} value={l._id}>{l.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <DialogContentText>
                This will recalculate the cached stock quantity for{' '}
                <strong>{item?.displayName ?? 'this item'}</strong> at the selected
                location by replaying its movement history. The cached quantity will be overwritten.
                Movement history is not changed.
              </DialogContentText>
            </>
          ) : (
            <Box sx={{ pt: 1 }}>
              <Alert severity={recomputeResult.difference === 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
                {recomputeResult.difference === 0
                  ? 'Balance was already correct — no change needed.'
                  : `Balance updated (difference: ${recomputeResult.difference > 0 ? '+' : ''}${recomputeResult.difference}).`}
              </Alert>
              {[
                { label: 'Before', value: recomputeResult.before },
                { label: 'After',  value: recomputeResult.after  },
              ].map(({ label, value }) => (
                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">{label}</Typography>
                  <Typography variant="body2" fontWeight={600}>{value}</Typography>
                </Box>
              ))}
            </Box>
          )}
          {recomputeError && <Alert severity="error" sx={{ mt: 2 }}>{recomputeError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecomputeOpen(false)} sx={{ textTransform: 'none' }} disabled={recomputing}>
            {recomputeResult ? 'Close' : 'Cancel'}
          </Button>
          {!recomputeResult && (
            <Button
              onClick={handleRecompute}
              variant="contained"
              disabled={recomputing || !recomputeLocationId}
              sx={{ textTransform: 'none', fontWeight: 600 }}
              startIcon={recomputing ? <CircularProgress size={16} color="inherit" /> : <RecalcIcon />}
            >
              {recomputing ? 'Recomputing…' : 'Recompute Balance'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)}>
        <DialogTitle>Delete inventory item?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will deactivate <strong>{item.displayName}</strong>. Movement history is preserved.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(false)} sx={{ textTransform: 'none' }} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            sx={{ textTransform: 'none' }}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryItemDetail;
